import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Article as DocIcon,
  TableChart as ExcelIcon,
  Description as TextIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  FolderOpen as PathIcon,
  Storage as SizeIcon,
  Category as TypeIcon,
  CloudUpload as UploadIcon,
  Update as UpdateIcon,
  Share as ShareIcon,
  Close as CloseIcon,
  DriveFileMove as MoveIcon,
  FileCopy as CopyIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  Bookmark as BookmarkIcon,
  Download as DownloadIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { formatLocalDateTime, formatRelativeTime } from '../../utils/dateUtils';
import { useGraphQL } from '../../apollo/useGraphQL';
import { getHistoricalInquiry } from '../../api/driveApi';

const FileDetailsPanel = ({ selectedItem, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const { executeQuery } = useGraphQL();

  // selectedItem이 변경될 때마다 썸네일 에러 상태 초기화
  useEffect(() => {
    setThumbnailError(false);
  }, [selectedItem?.uuid]);

  // 활동 내역 로드
  useEffect(() => {
    if (selectedItem?.id && activeTab === 1) {
      loadActivities();
    }
  }, [selectedItem?.id, activeTab]);

  const loadActivities = async () => {
    if (!selectedItem?.id) return;
    
    setLoadingActivities(true);
    try {
      const type = selectedItem.extension ? 'FILE' : 'FOLDER';
      const data = await getHistoricalInquiry(executeQuery, selectedItem.id, type, null);
      setActivities(data || []);
    } catch (error) {
      console.error('Error loading activities:', error);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  // Action을 한글로 매핑
  const getActionText = (action) => {
    const actionMap = {
      'insert': '업로드',
      'update': '수정',
      'rename': '이름 변경',
      'move': '이동',
      'softDelete': '삭제',
      'realDelete': '영구 삭제',
      'restore': '복원',
      'shared': '공유',
      'download': '다운로드',
      'copy': '복사',
      'bookmark': '즐겨찾기'
    };
    return actionMap[action] || action;
  };

  // Action에 따른 아이콘 반환
  const getActionIcon = (action) => {
    const iconProps = { sx: { fontSize: 16 } };
    const actionIconMap = {
      'insert': <UploadIcon {...iconProps} sx={{ color: '#34a853', fontSize: 16 }} />,
      'update': <UpdateIcon {...iconProps} sx={{ color: '#1a73e8', fontSize: 16 }} />,
      'rename': <EditIcon {...iconProps} sx={{ color: '#fbbc04', fontSize: 16 }} />,
      'move': <MoveIcon {...iconProps} sx={{ color: '#9c27b0', fontSize: 16 }} />,
      'softDelete': <DeleteIcon {...iconProps} sx={{ color: '#ea4335', fontSize: 16 }} />,
      'realDelete': <DeleteIcon {...iconProps} sx={{ color: '#d93025', fontSize: 16 }} />,
      'restore': <RestoreIcon {...iconProps} sx={{ color: '#34a853', fontSize: 16 }} />,
      'shared': <ShareIcon {...iconProps} sx={{ color: '#4285f4', fontSize: 16 }} />,
      'download': <DownloadIcon {...iconProps} sx={{ color: '#0f9d58', fontSize: 16 }} />,
      'copy': <CopyIcon {...iconProps} sx={{ color: '#ff6d00', fontSize: 16 }} />,
      'bookmark': <BookmarkIcon {...iconProps} sx={{ color: '#ff6d00', fontSize: 16 }} />
    };
    return actionIconMap[action] || <UpdateIcon {...iconProps} sx={{ fontSize: 16 }} />;
  };

  if (!selectedItem) {
    return null;
  }

  const getFileIcon = (extension, size = 48) => {
    const iconProps = { sx: { fontSize: size, color: '#5f6368' } };
    
    if (!extension) return <FolderIcon {...iconProps} sx={{ ...iconProps.sx, color: '#9aa0a6' }} />;
    
    const ext = extension.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
      return <ImageIcon {...iconProps} sx={{ ...iconProps.sx, color: '#34a853' }} />;
    }
    if (ext === 'pdf') {
      return <PdfIcon {...iconProps} sx={{ ...iconProps.sx, color: '#ea4335' }} />;
    }
    if (['doc', 'docx'].includes(ext)) {
      return <DocIcon {...iconProps} sx={{ ...iconProps.sx, color: '#4285f4' }} />;
    }
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return <ExcelIcon {...iconProps} sx={{ ...iconProps.sx, color: '#0f9d58' }} />;
    }
    if (['txt', 'md', 'rtf'].includes(ext)) {
      return <TextIcon {...iconProps} sx={{ ...iconProps.sx, color: '#5f6368' }} />;
    }
    
    return <FileIcon {...iconProps} />;
  };

  const getFileType = (extension) => {
    if (!extension) return '폴더';
    
    const ext = extension.toLowerCase();
    const typeMap = {
      'jpg': '이미지', 'jpeg': '이미지', 'png': '이미지', 'gif': '이미지',
      'pdf': 'PDF 문서',
      'doc': 'Word 문서', 'docx': 'Word 문서',
      'xls': 'Excel 스프레드시트', 'xlsx': 'Excel 스프레드시트', 'csv': 'CSV 파일',
      'ppt': 'PowerPoint 프레젠테이션', 'pptx': 'PowerPoint 프레젠테이션',
      'txt': '텍스트 파일', 'md': 'Markdown 파일',
      'zip': '압축 파일', 'rar': '압축 파일'
    };
    
    return typeMap[ext] || `${ext.toUpperCase()} 파일`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getDisplayName = (item) => {
    if (!item.extension) {
      return item.name;
    } else {
      return `${item.name}.${item.extension}`;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      sx={{
        width: 250,
        height: 'calc(88vh - 57px)',  // 파일/폴더 영역과 정확히 같은 높이 (툴바 + 패딩 고려)
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        mt: '72px', // 툴바 높이만큼 상단 위치 조정
        mr: 0.5, // 우측 여백
        ml: 1, // 좌측 여백 (파일/폴더 영역과 거리)
        borderRadius: '12px', // 둥근 모서리
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)' // 그림자 추가
      }}
    >
      {/* 1. Header with Name and Close Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 1,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Typography
          variant="h6"
          noWrap
          title={getDisplayName(selectedItem)}
          sx={{
            fontWeight: 500,
            fontSize: '16px',
            color: '#202124',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {getDisplayName(selectedItem)}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ 
            color: '#5f6368',
            '&:hover': { backgroundColor: '#f1f3f4' }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* 2. Thumbnail/Icon Section */}
      <Box sx={{ p: 1, px: 0.5 }}>
        {/* Thumbnail/Icon */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            {selectedItem.thumbnailUrl && !thumbnailError ? (
              <Box
                component="img"
                src={selectedItem.thumbnailUrl}
                alt={getDisplayName(selectedItem)}
                sx={{
                  width: '100%',
                  height: '180px',
                  objectFit: 'cover',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0'
                }}
                onError={() => {
                  setThumbnailError(true);
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '150px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0'
                }}
              >
                {getFileIcon(selectedItem.extension, 100)}
              </Box>
            )}
          </Box>
        </Box>

        {/* File Type Chip */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Chip
            label={getFileType(selectedItem.extension)}
            size="small"
            sx={{
              backgroundColor: '#e8f0fe',
              color: '#1a73e8',
              fontSize: '12px'
            }}
          />
        </Box>
      </Box>

      {/* 3. Tabs */}
      <Box>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              minHeight: 48,
              flex: 1
            }
          }}
        >
          <Tab label="세부정보" />
          <Tab label="활동" />
        </Tabs>
      </Box>

      {/* 4. Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 0 && (
          <Box>
            {/* Metadata Section */}
            <Box sx={{ p: 1 }}>
            <List disablePadding>
              {/* 파일 유형 */}
              <ListItem disablePadding sx={{ mb: 2 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <TypeIcon sx={{ color: '#5f6368', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary="유형"
                  secondary={getFileType(selectedItem.extension)}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: '#5f6368',
                    fontSize: '13px'
                  }}
                  secondaryTypographyProps={{
                    variant: 'body2',
                    color: '#202124',
                    fontSize: '14px'
                  }}
                />
              </ListItem>

              {/* 크기 */}
              {selectedItem.size !== 0 && (
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <SizeIcon sx={{ color: '#5f6368', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="크기"
                    secondary={formatFileSize(selectedItem.size)}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: '#5f6368',
                      fontSize: '13px'
                    }}
                    secondaryTypographyProps={{
                      variant: 'body2',
                      color: '#202124',
                      fontSize: '14px'
                    }}
                  />
                </ListItem>
              )}

              {/* 위치 */}
              <ListItem disablePadding sx={{ mb: 2 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PathIcon sx={{ color: '#5f6368', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary="위치"
                  secondary={selectedItem.parentName ? `${selectedItem.parentName}` : '사내 드라이브'}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: '#5f6368',
                    fontSize: '13px'
                  }}
                  secondaryTypographyProps={{
                    variant: 'body2',
                    color: '#202124',
                    fontSize: '14px'
                  }}
                />
              </ListItem>

              {/* 포인트 개수 */}
              {selectedItem.remark !== null && (
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ShareIcon sx={{ color: '#5f6368', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="포인트 개수"
                    secondary={selectedItem.remark}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: '#5f6368',
                      fontSize: '13px'
                    }}
                    secondaryTypographyProps={{
                      variant: 'body2',
                      color: '#202124',
                      fontSize: '14px'
                    }}
                  />
                </ListItem>
              )}

              {/* 소유자 */}
              <ListItem disablePadding sx={{ mb: 2 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PersonIcon sx={{ color: '#5f6368', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary="소유자"
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: '10px' }}>
                        {(selectedItem.createUserName || selectedItem.createUser || '?').charAt(0)}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontSize: '14px' }}>
                        {selectedItem.createUserName || selectedItem.createUser || '알 수 없음'}
                      </Typography>
                    </Box>
                  }
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: '#5f6368',
                    fontSize: '13px'
                  }}
                />
              </ListItem>

              <Divider sx={{ my: 2 }} />

              {/* 생성일 */}
              {selectedItem.createDate && (
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ScheduleIcon sx={{ color: '#5f6368', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="생성일"
                    secondary={formatLocalDateTime(selectedItem.createDate, true)}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: '#5f6368',
                      fontSize: '13px'
                    }}
                    secondaryTypographyProps={{
                      variant: 'body2',
                      color: '#202124',
                      fontSize: '14px'
                    }}
                  />
                </ListItem>
              )}

              {/* 수정일 */}
              {selectedItem.updateDate && (
                <ListItem disablePadding sx={{ mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <UpdateIcon sx={{ color: '#5f6368', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="수정일"
                    secondary={formatLocalDateTime(selectedItem.updateDate, true)}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: '#5f6368',
                      fontSize: '13px'
                    }}
                    secondaryTypographyProps={{
                      variant: 'body2',
                      color: '#202124',
                      fontSize: '14px'
                    }}
                  />
                </ListItem>
              )}
            </List>
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            {loadingActivities ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#1a73e8' }} />
              </Box>
            ) : activities.length > 0 ? (
              <Box sx={{ px: 2, py: 1 }}>
                {activities.map((activity, index) => (
                  <Box
                    key={activity.id || index}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      py: 1.5,
                      borderBottom: index === activities.length - 1 ? 'none' : '1px solid #f1f3f4',
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        borderRadius: 1,
                        mx: -1,
                        px: 1
                      }
                    }}
                  >
                    {/* 아이콘 */}
                    <Box sx={{ 
                      mr: 2, 
                      mt: 0.25,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#f8f9fa'
                    }}>
                      {getActionIcon(activity.action)}
                    </Box>

                    {/* 콘텐츠 */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* 액션과 사용자 */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 0.5, mb: 0.25 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '13px',
                            color: '#202124',
                            fontWeight: 400,
                            lineHeight: 1.4
                          }}
                        >
                          <Box component="span" sx={{ fontWeight: 500 }}>
                            {activity.createUser || '시스템'}
                          </Box>
                          님이 파일을 
                          <Box component="span" sx={{ fontWeight: 500, color: '#1a73e8' }}>
                            {getActionText(activity.action)}
                          </Box>
                          했습니다
                        </Typography>
                      </Box>

                      {/* 변경 사항 (이름 변경 등) */}
                      {activity.original && activity.changed && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#5f6368', 
                            fontSize: '12px',
                            display: 'block',
                            mb: 0.5,
                            lineHeight: 1.3
                          }}
                        >
                          {activity.original} → {activity.changed}
                        </Typography>
                      )}

                      {/* 시간 */}
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#5f6368', 
                          fontSize: '12px',
                          lineHeight: 1.3
                        }}
                      >
                        {formatRelativeTime(activity.createDate)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 2
              }}>
                <Box sx={{ 
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}>
                  <ScheduleIcon sx={{ color: '#9aa0a6', fontSize: 24 }} />
                </Box>
                <Typography variant="body2" sx={{ 
                  color: '#5f6368',
                  fontSize: '14px',
                  textAlign: 'center',
                  fontWeight: 400
                }}>
                  활동 내역이 없습니다
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: '#9aa0a6',
                  fontSize: '12px',
                  textAlign: 'center',
                  mt: 0.5
                }}>
                  이 파일의 활동 내역이 여기에 표시됩니다
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FileDetailsPanel;